import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoaderCircle } from 'lucide-react';
import * as yup from 'yup';

import { useResendVerificationEmail } from '@/api/auth/useResendVerificationEmail';
import AuthLayout from '@/component-library/layouts/AuthLayout/AuthLayout';
import { Button } from '@/component-library/primitives/Button/Button';
import CircleWarningIcon from '@/component-library/primitives/Icons/CircleWarningIcon/CircleWarningIcon';
import { Input } from '@/component-library/widgets/Input/Input';
import { AppRoutes } from '@/utils/routes/router';

import { useAuth } from '../../hooks/useAuth';

type FormData = {
  password: string;
};

const VerifyEmailPage = () => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - Type instantiation depth issue with i18next
  const { t } = useTranslation();
  const resendEmailMutation = useResendVerificationEmail();

  const [signInError, setSignInError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');

  // Get email from query params (from email link) or location state (from navigation)
  const emailFromQuery = searchParams.get('email');
  const passwordFromQuery = searchParams.get('password');
  const fromForgotPassword = searchParams.get('fromForgotPassword') === 'true';
  const email = emailFromQuery || location.state?.email || undefined;

  useEffect(() => {
    if (!email) {
      navigate(AppRoutes.signIn);
    }
  }, [email, navigate]);

  const schema = useMemo(() => {
    return yup.object().shape({
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - Type instantiation depth issue with i18next
      password: yup.string().required(t('CommonErrors.passwordRequired')),
    });
  }, [t]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      password: passwordFromQuery || '',
    },
  });

  // Pre-populate password field if it comes from query params
  useEffect(() => {
    if (passwordFromQuery) {
      setValue('password', passwordFromQuery);
    }
  }, [passwordFromQuery, setValue]);

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);

    try {
      if (!email) {
        navigate(AppRoutes.signIn);
        return;
      }
      const result = await signIn(email, data.password);
      if (result.isSignInSuccess) {
        navigate(AppRoutes.dashboard);
      } else if (result.message === 'NEW_PASSWORD_REQUIRED') {
        navigate({ pathname: '/create-new-password' });
      } else {
        setSignInError(result.message);
      }
    } catch (error) {
      console.error('Sign in error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t('VerifyEmailPage.title')}
      content={t('VerifyEmailPage.subtitle') + email}
      contentClass="text-center"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full space-y-4">
        <Input
          {...register('password')}
          error={errors.password?.message}
          type="password"
          label={t('VerifyEmailPage.passwordLabel')}
          cy-data="temporaryPassword"
        />
        {signInError && (
          <p className="text-errorRed-500 mt-2 text-xs font-medium flex flex-row items-center gap-2">
            <CircleWarningIcon /> {signInError}
          </p>
        )}

        <Button
          className="w-full bg-custom-blue hover:bg-custom-darkBlue text-white font-semibold py-3 rounded-lg transition-colors"
          disabled={isLoading}
        >
          {t('VerifyEmailPage.buttonSubmit')} {isLoading && <LoaderCircle height={20} className="animate-spin" />}
        </Button>
      </form>

      <div className="mt-6 text-center">
        {fromForgotPassword && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              {t('VerifyEmailPage.fromForgotPasswordMessage') ||
                "A new email with your temporary password has been sent. Please check your inbox. If you didn't receive it, you can resend it below."}
            </p>
          </div>
        )}
        <p className="text-custom-darkSilver text-sm">
          {t('VerifyEmailPage.resendEmailText')}
          <button
            type="button"
            onClick={async () => {
              if (!email) {
                setResendError('Email is required');
                return;
              }
              setResendError('');
              setResendSuccess(false);
              try {
                await resendEmailMutation.mutateAsync({ email });
                setResendSuccess(true);
                setTimeout(() => setResendSuccess(false), 5000);
              } catch (error: unknown) {
                const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
                const errorMessage =
                  axiosError?.response?.data?.message ||
                  axiosError?.message ||
                  'Failed to resend verification email. Please try again.';
                setResendError(errorMessage);
                setTimeout(() => setResendError(''), 5000);
              }
            }}
            disabled={resendEmailMutation.isLoading || !email}
            className="ml-1 text-custom-blue hover:text-custom-darkBlue transition-colors no-underline bg-transparent border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resendEmailMutation.isLoading ? (
              <>
                {t('VerifyEmailPage.resendEmail')} <LoaderCircle height={14} className="animate-spin inline" />
              </>
            ) : (
              t('VerifyEmailPage.resendEmail')
            )}
          </button>
        </p>
        {resendSuccess && (
          <p className="text-green-600 text-sm mt-2">
            {t('VerifyEmailPage.resendSuccess') || 'Verification email has been resent successfully!'}
          </p>
        )}
        {resendError && (
          <p className="text-errorRed-500 text-sm mt-2 flex items-center justify-center gap-2">
            <CircleWarningIcon /> {resendError}
          </p>
        )}
      </div>
    </AuthLayout>
  );
};

export default VerifyEmailPage;
